import { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import { getPhotoStream, getPhotoUrl } from "../services/photoService";
import { Prediction } from "../types/prediction";
import { Photo } from "../types/photo";
import { PiScanBold } from "react-icons/pi";
import { optimizeImage } from "../utilities/imageOptimization";

export type PhotoItemHandle = {
  togglePrediction: () => void;
};

const PhotoItem = forwardRef<PhotoItemHandle, { photo: Photo }>(({ photo }, ref) => {
  const [photoUrl, setPhotoUrl] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [scaledPredictions, setScaledPredictions] = useState<Prediction[]>([]);
  const imageRef = useRef<HTMLImageElement>(null);
  const [showPredictions, setShowPredictions] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    togglePrediction() {
      setShowPredictions(prev => !prev);
    }
  }));

  function transformBoxForOrientation(
    [rawXmin, rawYmin, rawXmax, rawYmax]: [number, number, number, number],
    orientation: number,
    webW: number,
    webH: number,
    scaleX: number,
    scaleY: number
  ): [number, number, number, number] {
    const dx1 = rawXmin * scaleX;
    const dy1 = rawYmin * scaleY;
    const dx2 = rawXmax * scaleX;
    const dy2 = rawYmax * scaleY;

    let xMin: number, yMin: number, xMax: number, yMax: number;

    switch (orientation) {
      case 2: // flip H
        xMin = webW - dx2; yMin = dy1;
        xMax = webW - dx1; yMax = dy2;
        break;
      case 3: // rotate 180°
        xMin = webW - dx2; yMin = webH - dy2;
        xMax = webW - dx1; yMax = webH - dy1;
        break;
      case 4: // flip V
        xMin = dx1; yMin = webH - dy2;
        xMax = dx2; yMax = webH - dy1;
        break;
      case 5: // transpose (TL↔BR diagonal)
        xMin = dy1; yMin = dx1;
        xMax = dy2; yMax = dx2;
        break;
      case 6: // rotate 90° CW
        xMin = webW - dy2; yMin = dx1;
        xMax = webW - dy1; yMax = dx2;
        break;
      case 7: // transverse (TR↔BL diagonal)
        xMin = webW - dy2; yMin = webH - dx2;
        xMax = webW - dy1; yMax = webH - dx1;
        break;
      case 8: // rotate 270° CW (or 90° CCW)
        xMin = dy1; yMin = webH - dx2;
        xMax = dy2; yMax = webH - dx1;
        break;
      default: // orientation 1: no change
        xMin = dx1; yMin = dy1;
        xMax = dx2; yMax = dy2;
        break;
    }

    return [xMin, yMin, xMax, yMax];
  }

  // Fetch the photo
  useEffect(() => {
    setShowPredictions(false);
    setScaledPredictions([]);
    setLoading(true);
    const fetchPhoto = async () => {
      try {
        const url = await getPhotoUrl(photo.photoId);
        // For photos with predictions, use original size to maintain accurate detection boxes
        // Only optimize if no predictions
        const optimizedUrl = photo.predictions && photo.predictions.length > 0 
          ? url 
          : optimizeImage(url, 'gallery');
        setPhotoUrl(optimizedUrl);
      } catch (err) {
        setError("Failed to fetch photo");
      } finally {
        setLoading(false);
      }
    };
    fetchPhoto();
  }, [photo.photoId]);

  useEffect(() => {
    if (!imageRef.current || !photo.predictions) return;

    const img = imageRef.current;

    const recalculateBoxes = () => {
      if (!img.naturalWidth || !img.naturalHeight) return;
      const scaleX = img.width / img.naturalWidth;
      const scaleY = img.height / img.naturalHeight;

      const scaled = photo.predictions.map(pred => ({
        ...pred,
        box: transformBoxForOrientation(
          pred.box as [number, number, number, number],
          photo.orientation || 1,
          img.width,
          img.height,
          scaleX,
          scaleY
        )
      }));

      setScaledPredictions(scaled);
    };

    recalculateBoxes();

    const resizeObserver = new ResizeObserver(() => {
      recalculateBoxes();
    });

    resizeObserver.observe(img);

    return () => {
      resizeObserver.disconnect();
    };
  }, [photo.predictions, photo.orientation, photoUrl, imageRef]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div className="relative w-full group">
      <img
        src={photoUrl}
        ref={imageRef}
        className="w-full h-auto rounded-lg"
        alt="Uploaded"
      />
      <div className="photo-actions opacity-0 group-hover:opacity-100 transition-opacity">
        {scaledPredictions.length > 0 && (
          <button
            className="photo-action-btn transition-all rounded-full predict-btn absolute top-2 right-2 bg-primary/80 p-2 shadow-md hover:bg-primary hover:ring-white hover:ring-2"
            onClick={(e) => {
              e.preventDefault();
              setShowPredictions(prev => !prev);
            }}
          >
            <PiScanBold className="h-4 w-4 text-white" />

            {/* Tooltip */}
            <div className="tooltip absolute bottom-full right-0 mb-2 hidden px-2 py-1 text-xs text-white bg-gray-800 rounded-md shadow-md">
              {showPredictions ? "Hide Predictions" : "Show Predictions"}
            </div>
          </button>
        )}
      </div>

      {showPredictions && scaledPredictions.map((pred, index) => {
        const [xMin, yMin, xMax, yMax] = pred.box;
        const width = xMax - xMin;
        const height = yMax - yMin;

        return (
          <div
            key={index}
            className="absolute border-2 border-red-500 pointer-events-none"
            style={{
              left: `${xMin}px`,
              top: `${yMin}px`,
              width: `${width}px`,
              height: `${height}px`,
            }}
          >
            <span className="absolute bg-red-500 bg-opacity-30 text-white text-xs p-1">
              {(pred.score * 100).toFixed(1)}%
            </span>
          </div>
        );
      })}
    </div>
  );
});

export default PhotoItem;
